import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, siteInfo } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading font-bold text-2xl text-foreground-950 mb-2">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700" onClick={handleClick}>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  if (relatedArticles.length < 3) {
    const otherArticles = articles
      .filter((a) => a.categorySlug !== article.categorySlug && a.id !== article.id)
      .slice(0, 3 - relatedArticles.length);
    relatedArticles.push(...otherArticles);
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.id}`,
    },
    keywords: article.tags.join(','),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: siteInfo.url,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `${siteInfo.url}/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 明星黑料大全`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},明星黑料,娱乐圈黑料`}
        canonicalUrl={`${siteInfo.url}/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        schemaJson={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <Navbar />

      <main className="min-h-screen pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.category, path: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap"
                onClick={handleClick}
              >
                {article.category}
              </Link>
              {article.isNew && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                  最新
                </span>
              )}
              {article.isHot && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                  热门
                </span>
              )}
            </div>

            <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/70">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views >= 100000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views.toLocaleString()}阅读`}
              </span>
            </div>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 明星黑料大全`}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed mb-10 article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap items-center gap-2 pt-6 border-t border-background-200/70 mb-10">
            <span className="text-sm text-foreground-600 mr-2">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-700 border border-background-200/70"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between py-6 border-t border-b border-background-200/70 mb-10">
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-600">分享：</span>
              <a
                href="#share-weibo"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors"
                rel="nofollow"
              >
                <i className="ri-weibo-line"></i>
              </a>
              <a
                href="#share-wechat"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors"
                rel="nofollow"
              >
                <i className="ri-wechat-line"></i>
              </a>
              <a
                href="#share-link"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors"
                rel="nofollow"
              >
                <i className="ri-link"></i>
              </a>
            </div>
            <Link
              to={`/category/${article.categorySlug}`}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
              onClick={handleClick}
            >
              更多{article.category} <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} article={related} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}